import Modal from '@shared/common/Modal';
import { useEffect, useMemo, useState } from 'react';
import toast, { LoaderIcon } from 'react-hot-toast';
import { WorkspaceMembersForUnit } from '@entities/models/workspace';
import { MemberListItem } from './MemberListItem';
import { InviteByEmail } from './InviteByEmail';
import {
  getMembersForUnit as getDocumentMembers,
  modifyUserAccessLevel,
  modifyWorkspaceMembersAccessLevel,
} from '@app/services/share.service';
import './sharedoc.styles.css';
import { AccessTypes } from './types';
import { successToastOptions, errorToastOptions } from '@shared/common/Toast';
import { track } from '@amplitude/analytics-browser';
import { useDispatch, useSelector } from 'react-redux';
import {
  getModalState,
  updateShareModalState,
} from '@app/redux/features/modalsSlice';
import { ShareDocViaLink } from './ShareDocViaLink';
import Switch from "react-switch";
import { useGetUnitQuery } from '@app/queries/unit/useGetUnitQuery';
import { getUnit } from '@app/services/unit.service';
import { useWorkspaceContext } from '@app/context/workspace/context';
import { usePermissionsContext } from '@app/context/permissionsContext/permissionsContext';

export function ShareDoc() {
  const dispatch = useDispatch();
  const { activeWorkspace } = useWorkspaceContext();
  const { canShareUnit } = usePermissionsContext();
  const [members, setMembers] = useState<WorkspaceMembersForUnit>([]);
  const [isInviting, setIsInviting] = useState<boolean>(false);
  const [viaLinkIsVisible, setViaLinkIsVisible] = useState<boolean>(true);

  const { isOpen, title, unitId } = useSelector(getModalState).shareModalState;
  const { data } = useGetUnitQuery({ unitId: unitId, workspaceId: activeWorkspace?.id }, getUnit);
  const workspaceMembersAccess = data?.workspaceMembersAccess;

  const emailInvitingIsDisabled = useMemo(
    () => !canShareUnit(unitId),
    [canShareUnit, unitId],
  );

  useEffect(() => {
    if (isOpen) {
      track('document_share_popup', { option: 'share' });
    }
  }, [isOpen]);

  useEffect(() => {
    async function getMembers() {
      if (!unitId) return;
      const res = await getDocumentMembers(unitId);
      setMembers(res ?? []);
    }
    getMembers();
  }, [unitId, isOpen]);

  const handleEditMember = (access: AccessTypes, id: string) => {
    if (!unitId) return;
    try {
      modifyUserAccessLevel(access, id, unitId);
      toast.success('Access level successfully changed!', successToastOptions);
    } catch (e) {
      toast.error('Unable change access level', errorToastOptions);
    }
  };

  const handleEditWorkspaceMembers = (access: AccessTypes, id: string) => {
    if (!unitId) return;
    try {
      modifyWorkspaceMembersAccessLevel(access, id, unitId);
      toast.success('Access level successfully changed!', successToastOptions);
    } catch (e) {
      toast.error('Unable change access level', errorToastOptions);
    }
  };

  const handleClose = () => {
    track('document_share_close');
    dispatch(
      updateShareModalState({
        isOpen: false,
        title: title,
        unitId: '',
      }),
    );
  };

  return (
    <Modal
      title={title ? title : 'Share doc'}
      closeModal={handleClose}
      modalIsOpen={isOpen}
      wrapChildren={false}
      userClassName='w-638 !p-10 !animate-none'
      titleClassName='!text-20-16 !font-medium'
      closeBtnClassName='absolute top-6 right-6'
    >
      {isInviting && (
        <div className='absolute inset-0 flex items-center justify-center bg-[#ffffff99] z-10'>
          <LoaderIcon
            style={{ width: 30, height: 30 }}
            className='animate-spin'
          />
        </div>
      )}
      <div
        className='flex flex-col full-width mt-6 z-0'
        contentEditable={false}
      >
        <InviteByEmail
          unitId={unitId}
          isInviting={isInviting}
          setIsInviting={setIsInviting}
          members={members}
          emailInvitingIsDisabled={emailInvitingIsDisabled}
        />
        <div className="flex justify-between mt-8 mb-3 cursor-pointer" onClick={() => setViaLinkIsVisible(value => !value)}>
          <span className='text-[14px] font-medium'>Share to web</span>
          <Switch
            checked={viaLinkIsVisible}
            onChange={() => undefined}
            uncheckedIcon={false}
            checkedIcon={false}
            onColor={"#4A86FF"}
            height={24}
            width={44}
            className={`react-switch ${viaLinkIsVisible ? "active" : "inactive"}`}
          />
        </div>
        {viaLinkIsVisible && <ShareDocViaLink />}
        <div className='width-full border  border-solid border-text10 my-5' />
        <ul>
          <span className='text-[14px] font-medium'>Shared with</span>
          <li>
            <MemberListItem
              showLogo
              onChange={handleEditWorkspaceMembers}
              name={"All workspace members"}
              email={"All workspace members"}
              memberId={""}
              access={workspaceMembersAccess}
              toTop
            />
          </li>
          {members?.filter(item => item.access !== workspaceMembersAccess)?.map((member) => (
            <li key={member.user.id || member.user.name}>
              <MemberListItem
                onChange={handleEditMember}
                imageSrc={member.user.image}
                name={member.user.name}
                email={member.user.email}
                memberId={member.user.id}
                role={member.role}
                access={member.access}
                toTop
              />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
